import React, { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Quote, Star } from "lucide-react";
import { useLang } from "@/contexts/LangContext";

const ease = [0.22, 1, 0.36, 1];

const BASE = "https://reinventemercadeo.com/wp-content/uploads/2026/03/";
const AVATARS = {
  "Andrés R.": BASE + "testimonial-5.png",
  "Julio V.":  BASE + "logo-oficial-JCV-solo-cara.png",
  "Laura G.":  BASE + "testimonial-4.png",
  "Carlos M.": BASE + "testimonial-3.png",
};

export default function Testimonials() {
  const { t } = useLang();
  const items = t.testimonials.items;
  const sectionRef = useRef(null);

  // Alternating parallax between the two columns
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  const yLeft = useTransform(scrollYProgress, [0, 1], [20, -20]);
  const yRight = useTransform(scrollYProgress, [0, 1], [56, -56]);

  return (
    <section
      ref={sectionRef}
      id="testimonials"
      data-testid="testimonials-section"
      className="relative py-28 lg:py-36 overflow-hidden"
    >
      <div className="absolute inset-0 bg-radial-orange opacity-40 pointer-events-none" />
      <div className="mx-auto max-w-7xl px-5 relative">
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease }}
          className="max-w-3xl mb-14"
        >
          <div className="text-[11px] sm:text-xs uppercase tracking-[0.24em] text-brand-blue font-mono-tight mb-4">
            {t.testimonials.eyebrow}
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[1.02]">
            {t.testimonials.title} <br />
            <span className="text-gradient-brand">{t.testimonials.title2}</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
          {items.map((it, i) => (
            <motion.div key={it.name} style={{ y: i % 2 === 0 ? yLeft : yRight }}>
              <TestimonialCard item={it} index={i} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Avatar({ name }) {
  const [err, setErr] = useState(false);
  const src = AVATARS[name];
  if (src && !err) {
    return (
      <img
        src={src}
        alt={name}
        onError={() => setErr(true)}
        className="h-10 w-10 rounded-full object-cover object-top shrink-0 ring-1 ring-white/20 bg-white/5"
      />
    );
  }
  return (
    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-brand-blue/60 to-brand-orange/60 grid place-items-center font-display font-bold text-white text-sm shrink-0">
      {name.split(" ").map((p) => p[0]).join("")}
    </div>
  );
}

function TestimonialCard({ item, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, ease, delay: index * 0.06 }}
      whileHover={{ y: -4 }}
      data-testid={`testimonial-${index}`}
      className="group relative h-full rounded-3xl glass p-7 lg:p-9 hover:border-white/20 transition overflow-hidden"
    >
      <Quote className="absolute top-6 right-6 h-8 w-8 text-white/8" strokeWidth={1.5} />
      <div className="flex gap-0.5 mb-5">
        {[...Array(5)].map((_, idx) => (
          <Star key={`star-${idx}`} className="h-3.5 w-3.5 fill-brand-orange text-brand-orange" />
        ))}
      </div>
      <p className="text-[15px] sm:text-base lg:text-[17px] text-white/80 leading-relaxed">"{item.quote}"</p>
      <div className="mt-7 pt-6 border-t border-white/8 flex items-center gap-3">
        <Avatar name={item.name} />

        <div>
          <div className="text-[13.5px] font-semibold text-white tracking-tight">{item.name}</div>
          <div className="text-[11.5px] text-white/55 font-mono-tight">{item.role}</div>
        </div>
      </div>
    </motion.div>
  );
}
